import { useEffect, useRef, useState } from "react";
import {
  Button,
  Heading,
  HtmlCheckbox,
  Icon,
  Text,
  Textarea,
} from "@ttc3k/trekker";
import { Cart } from "iconoir-react";
import { Box, Center, Flex, VStack } from "styled-system/jsx";
import { CheckoutSummary, ValidatePromoCodeForm } from "widgets/booker/shared";
import {
  calculateProRatedAmount,
  formatPrice,
  getMultilingualString,
  getResolvedProductSellingConfig,
} from "shared/utils";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import {
  DEFAULT_STRIPE_FIXED_CENTS,
  DEFAULT_STRIPE_PERCENT,
  estimateIncludedFees,
} from "@shared/fees";
import {
  EnumBookerRenewalSettingsRenewalMode,
  EnumDiscountType,
  EnumProductType,
  OrderDeliveryMethodInput,
} from "gql/generated";
import { OrderSuccessPage } from "../OverlayPages";
import { useCatalogCartItemRenderer } from "../MerchCatalog/catalogCartEditing";
import { useCheckout } from "./useCheckout";
import { useCheckoutRequirements } from "./useCheckoutRequirements";
import { CheckoutChecklist } from "./CheckoutChecklist";
import { useTranslation } from "react-i18next";
import { WhereaboutsLogo } from "shared/components";

const MAX_GUEST_MESSAGE_LENGTH = 2000;

export const Checkout = () => {
  const {
    product,
    booker,
    readOnlySelection,
    paymentRequestGrandTotal,
    paymentRequestSubtotal,
    paymentRequestTaxAmount,
    paymentRequestTaxLines,
    paymentRequestCustomerFee,
    paymentRequestAdditionalLineItems,
    language,
    membershipIntent,
  } = useBookerContext();
  const renderCatalogCartItem = useCatalogCartItemRenderer();
  const {
    subTotal,
    grandTotal,
    depositTotal,
    tax,
    taxLines,
    cart,
    customerFee,
    customer,
    selectedTierId,
    paymentMethodId,
    effectiveTaxExempt,
    renewableFeeQuote,
    businessField,
    shippingTotal,
    delivery,
    deliveryAvailable,
    autoRenewOptIn,
    updateAutoRenewOptIn,
    messageFromGuest,
    updateMessageFromGuest,
  } = useCartContext();
  const {
    isComplete: isBookerComplete,
    items: checklistItems,
    totalDue,
    isOfflineLikePayment,
    isBookingRequest,
    isInvoicedAfterService,
    isMonthlyStatement,
  } = useCheckoutRequirements();
  const {
    onCheckoutSubmit,
    error,
    errorMsg,
    loading,
    orderComplete,
    submissionLocked,
    discountDisplayData,
    setStatus,
  } = useCheckout();
  const { t } = useTranslation("booker");
  const sellingConfig = getResolvedProductSellingConfig(
    product as typeof product & { sellingConfig?: any },
  );

  const isRenewableFee = product?.type === EnumProductType.RenewableFee;
  const isRenewalInvoiceMode =
    booker?.renewalSettings?.renewalMode ===
    EnumBookerRenewalSettingsRenewalMode.Invoice;
  const isCardPayment =
    paymentMethodId !== "direct_debit" && !isOfflineLikePayment;
  const canOptInToAutoRenew =
    isRenewableFee && !isRenewalInvoiceMode && isCardPayment;
  const willAutoRenew = canOptInToAutoRenew && autoRenewOptIn;

  const isDepositEnabled = booker?.paymentMetadata?.depositSettings?.enabled;
  const invoiceDueDays = booker?.paymentTiming?.invoiceDueDays;

  // Optional "fees are already in the price" note. Never shown next to a
  // separate customer fee line (it would double count) or on invoices.
  const feeAbsorption = booker?.paymentMetadata?.feeAbsorption;
  const includedFeesEstimate =
    feeAbsorption?.showBreakdownInCheckout &&
    paymentRequestGrandTotal == null &&
    !customerFee
      ? estimateIncludedFees(grandTotal, {
          applicationPercent: booker?.paymentMetadata?.applicationPercentage ?? 0,
          stripePercent: feeAbsorption.stripePercent ?? DEFAULT_STRIPE_PERCENT,
          stripeFixedCents:
            feeAbsorption.stripeFixedCents ?? DEFAULT_STRIPE_FIXED_CENTS,
        })
      : 0;

  const selectedTier = selectedTierId
    ? sellingConfig.options.find((option: any) => option._id === selectedTierId)
    : null;

  // Billing label for display
  const billingIntervalMonths =
    sellingConfig.recurring?.billingIntervalMonths ?? 1;
  const renewalType = sellingConfig.recurring?.renewalType;
  const fixedRenewalMonth = sellingConfig.recurring?.fixedRenewalMonth;
  const fixedRenewalDay = sellingConfig.recurring?.fixedRenewalDay;
  const isFixedDate = renewalType === "FIXED_DATE";

  const monthNames = [
    "", "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const fixedRenewalMonthName = monthNames[fixedRenewalMonth ?? 0] ?? "";

  const intervalLabel = isFixedDate
    ? t("CHECKOUT.ANNUAL")
    : billingIntervalMonths === 1
      ? t("CHECKOUT.MONTHLY")
      : billingIntervalMonths === 12
        ? t("CHECKOUT.ANNUAL")
        : `${t("CHECKOUT.EVERY")} ${billingIntervalMonths} ${t("CHECKOUT.MONTHS")}`;

  const fullPrice = selectedTier
    ? selectedTier.price
    : sellingConfig.basePrice;
  const quote = renewableFeeQuote.active ? renewableFeeQuote.data : null;
  const proRatedPrice = quote
    ? quote.chargeAmount
    : isRenewableFee && !renewableFeeQuote.active
      ? calculateProRatedAmount(fullPrice, sellingConfig.recurring)
      : fullPrice;
  const isProRated = quote
    ? quote.pricingKind === "PRO_RATED"
    : proRatedPrice !== fullPrice;
  const formatQuoteDate = (isoDay?: string | null) => {
    if (!isoDay) return "";
    const [y, m, d] = isoDay.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <VStack gap={"400"} alignItems={"flex-start"} w={"full"}>
      <Heading size={"h5"} display={"flex"} gap={"100"} alignItems={"center"}>
        <Icon Element={Cart} size={"icons.lg"} />
        {membershipIntent
          ? t(`CHECKOUT.TITLE_${membershipIntent.kind}`, {
              membership: membershipIntent.membershipNomenclature,
              member: membershipIntent.memberNomenclature,
            })
          : t("CHECKOUT.TITLE")}
      </Heading>

      <VStack gap={"300"} alignItems={"flex-start"} w={"full"}>
        {!readOnlySelection &&
          grandTotal > 0 &&
          booker?.discounts?.some(
            d => d.type === EnumDiscountType.PromoCode && d.isActive,
          ) && <ValidatePromoCodeForm />}

        {isRenewableFee && (
          <Flex flexDirection="column" gap="200" w="full">
            <Flex flexDirection="column" gap="50">
              <Text visual="bodySemiBold" color="text.dark">
                {isFixedDate
                  ? t("CHECKOUT.FIXED_RENEWAL_DATE", {
                      month: fixedRenewalMonthName,
                      day: fixedRenewalDay,
                    })
                  : `${t("CHECKOUT.BILLING_INTERVAL")}: ${intervalLabel}`}
              </Text>
              {selectedTier && (
                <Text visual="smallMedium" color="text.mid">
                  {getMultilingualString(selectedTier.name)} — $
                  {formatPrice(selectedTier.price)}
                </Text>
              )}
              {quote && quote.listingLines.length > 1 && (
                <Flex flexDirection="column" gap="25">
                  {quote.listingLines.flatMap(line => line ? [line] : []).map(line => (
                    <Text
                      key={line.index}
                      visual="smallRegular"
                      color="text.mid"
                    >
                      {t("CHECKOUT.BUSINESS_LINE", {
                        name:
                          businessField.includedBusinesses?.[line.index]
                            ?.businessName ||
                          t("CHECKOUT.BUSINESS_FALLBACK", {
                            n: line.index + 1,
                          }),
                        amount: `$${formatPrice(line.amount)}`,
                      })}
                    </Text>
                  ))}
                  {quote.additionalBusinessSavings > 0 && (
                    <Text visual="smallSemiBold" color="text.dark">
                      {t("CHECKOUT.MULTI_BUSINESS_SAVINGS", {
                        amount: `$${formatPrice(quote.additionalBusinessSavings)}`,
                      })}
                    </Text>
                  )}
                </Flex>
              )}
              {isProRated && (
                <Text visual="smallSemiBold" color="text.dark">
                  {t("CHECKOUT.PRO_RATED_AMOUNT", {
                    amount: `$${formatPrice(proRatedPrice)}`,
                    fullAmount: `$${formatPrice(quote?.fullAmount ?? fullPrice)}`,
                  })}
                </Text>
              )}
              {renewableFeeQuote.loading && (
                <Text visual="smallRegular" color="text.mid">
                  {t("CHECKOUT.CALCULATING_PRICE")}
                </Text>
              )}
              {renewableFeeQuote.error && (
                <Text visual="smallRegular" color="text.mid">
                  {t("CHECKOUT.PRICE_CONFIRMED_AT_PAYMENT")}
                </Text>
              )}
              {quote?.pricingKind === "EARLY_PURCHASE" && (
                <>
                  <Text visual="smallSemiBold" color="text.dark">
                    {t("CHECKOUT.EARLY_PURCHASE_NOTICE", {
                      periodEnd: formatQuoteDate(quote.periodEnd),
                      count: quote.bonusDays,
                      renewalDate: formatQuoteDate(quote.freeDaysUntil),
                    })}
                  </Text>
                  <Text visual="smallRegular" color="text.mid">
                    {t("CHECKOUT.EARLY_PURCHASE_EXISTING_MEMBER_HINT")}
                  </Text>
                </>
              )}
              {quote?.pricingKind === "RENEWAL_NEXT_PERIOD" && (
                <Text visual="smallSemiBold" color="text.dark">
                  {t("CHECKOUT.RENEWAL_NEXT_PERIOD_NOTICE", {
                    periodEnd: formatQuoteDate(quote.periodEnd),
                  })}
                </Text>
              )}
            </Flex>
            {canOptInToAutoRenew && (
              <HtmlCheckbox
                label={t("CHECKOUT.AUTO_RENEW_OPT_IN", {
                  interval: intervalLabel,
                })}
                checked={autoRenewOptIn}
                onChange={e => updateAutoRenewOptIn(e.target.checked)}
              />
            )}
            <Text visual="smallRegular" color="text.mid">
              {!willAutoRenew
                ? t("CHECKOUT.RENEWAL_INVOICE_MODE_EXPLAINER", {
                    interval: intervalLabel,
                  })
                : t("CHECKOUT.RENEWAL_AUTO_CHARGE_EXPLAINER", {
                    interval: intervalLabel,
                  })}
            </Text>
          </Flex>
        )}

        <DiscountAppliedBanner discounts={discountDisplayData} />
        <CheckoutSummary
          cart={cart}
          subTotal={paymentRequestSubtotal ?? subTotal}
          grandTotal={paymentRequestGrandTotal ?? grandTotal}
          tax={paymentRequestTaxAmount ?? tax}
          taxLines={
            paymentRequestGrandTotal != null
              ? (paymentRequestTaxLines ?? null)
              : taxLines
          }
          customerFee={
            paymentRequestGrandTotal != null
              ? (paymentRequestCustomerFee ?? 0)
              : customerFee
          }
          discounts={discountDisplayData}
          depositSettings={
            paymentRequestGrandTotal != null
              ? undefined
              : booker?.paymentMetadata?.depositSettings
          }
          depositTotal={depositTotal}
          includedFeesEstimate={includedFeesEstimate}
          shippingTotal={
            paymentRequestGrandTotal == null &&
            delivery?.method === OrderDeliveryMethodInput.Ship &&
            deliveryAvailable === true
              ? shippingTotal
              : null
          }
          mainProduct={product ?? undefined}
          additionalLineItems={paymentRequestAdditionalLineItems}
          taxExemptionClaimed={effectiveTaxExempt}
          hideMainProduct={!!booker?.isInvoiceTemplate}
          selectedTierId={selectedTierId}
          language={language}
          renderCartItem={renderCatalogCartItem}
        />
        <Box
          borderTopWidth={"2px"}
          borderTopStyle={"solid"}
          borderTopColor={"border.dark"}
          w={"full"}
        />
        <Flex w={"full"} justifyContent={"space-between"} alignItems={"center"}>
          <Box>
            <Text visual={"bodySemiBold"} color={"text.dark"}>
              {isBookingRequest
                ? t("CHECKOUT.REQUEST.DUE_AFTER_APPROVAL")
                : isMonthlyStatement
                  ? t("CHECKOUT.AFTER_SERVICE.ON_STATEMENT")
                  : isInvoicedAfterService
                    ? t("CHECKOUT.AFTER_SERVICE.INVOICED_LATER")
                    : t("CHECKOUT.TOTAL_DUE")}
              {isDepositEnabled && !isBookingRequest && !isInvoicedAfterService
                ? " " + t("CHECKOUT.TODAY")
                : ""}
            </Text>
            <Text visual={"smallMedium"} color={"text.light"}>
              {t("CHECKOUT.TAXES_INCLUDED")}
            </Text>
          </Box>
          <AnimatedTotal
            totalDue={isInvoicedAfterService ? grandTotal : totalDue}
          />
        </Flex>
        {isInvoicedAfterService && (
          <Text visual="smallRegular" color="text.mid">
            {isMonthlyStatement
              ? t("CHECKOUT.AFTER_SERVICE.NO_PAYMENT_NOW_STATEMENT")
              : t("CHECKOUT.AFTER_SERVICE.NO_PAYMENT_NOW")}
          </Text>
        )}
        {isBookingRequest && (
          <Flex flexDirection="column" gap="200" w="full">
            <Text visual="smallRegular" color="text.mid">
              {booker?.approvalSettings?.messageToGuest
                ? getMultilingualString(
                    booker.approvalSettings.messageToGuest,
                    language,
                  ) || t("CHECKOUT.REQUEST.NO_PAYMENT_NOW")
                : t("CHECKOUT.REQUEST.NO_PAYMENT_NOW")}
            </Text>
            <Textarea
              label={t("CHECKOUT.REQUEST.MESSAGE_LABEL")}
              placeholder={t("CHECKOUT.REQUEST.MESSAGE_PLACEHOLDER")}
              name="messageFromGuest"
              rows={3}
              maxLength={MAX_GUEST_MESSAGE_LENGTH}
              bg="white"
              value={messageFromGuest}
              onChange={e => updateMessageFromGuest(e.target.value)}
            />
          </Flex>
        )}
        {booker?.customizations?.showCheckoutChecklist && (
          <CheckoutChecklist
            items={checklistItems}
            heading={
              isBookingRequest
                ? t("CHECKOUT.CHECKLIST.HEADING_REQUEST")
                : totalDue === 0 || isInvoicedAfterService
                  ? t("CHECKOUT.CHECKLIST.HEADING_ORDER")
                  : t("CHECKOUT.CHECKLIST.HEADING_PAY")
            }
            readyHeading={t("CHECKOUT.CHECKLIST.HEADING_READY")}
          />
        )}
      </VStack>

      <Box w={"full"}>
        <Button
          size={"lg"}
          w={"full"}
          disabled={!isBookerComplete || submissionLocked}
          onClick={onCheckoutSubmit}
          isLoading={loading}
          type="button"
        >
          {isBookingRequest
            ? t("CHECKOUT.REQUEST.BUTTON")
            : isInvoicedAfterService
            ? t("CHECKOUT.AFTER_SERVICE.BUTTON")
            : isOfflineLikePayment
            ? t("CHECKOUT.SEND_INVOICE", {
                amount: formatPrice(totalDue),
                email: customer.email,
              })
            : totalDue === 0
              ? t("CHECKOUT.ORDER_NOW")
              : <>{t("CHECKOUT.PAY")}&nbsp;${formatPrice(totalDue)}</>}
        </Button>
        <Flex
          flexDir={"column"}
          justifyContent={"space-between"}
          height={"148px"}
        >
          <Text
            mt={"100"}
            visual={"smallRegular"}
            color={!error ? "text.light" : "text.error.mid"}
            lineClamp={4}
          >
            {error
              ? t("CHECKOUT.PAYMENT_ERROR", { errorMsg })
              : isBookingRequest
                ? t("CHECKOUT.REQUEST.FOOTER")
                : isInvoicedAfterService
                  ? invoiceDueDays
                    ? t("CHECKOUT.AFTER_SERVICE.FOOTER_DUE_DAYS", {
                        count: invoiceDueDays,
                      })
                    : t("CHECKOUT.AFTER_SERVICE.FOOTER")
                  : t("CHECKOUT.PAYMENT_MESSAGE")}
          </Text>
          <Center width={"full"} flexDir={"column"} gap={"100"}>
            <Text color="text.light" visual="smallRegular">
              {t("POWERED_BY")}
            </Text>
            <WhereaboutsLogo width={110} height="auto" />
          </Center>
        </Flex>
      </Box>
      <OrderSuccessPage
        open={orderComplete}
        onBackToSiteClick={() =>
          setStatus({
            error: false,
            errorMsg: "",
            loading: false,
            orderComplete: false,
            submissionLocked: false,
          })
        }
      />
    </VStack>
  );
};

const DISCOUNT_KEYFRAMES = `
@keyframes wa-discount-slide-in { 0% { opacity: 0; transform: translateY(-8px); } 100% { opacity: 1; transform: translateY(0); } }
@keyframes wa-discount-fade-out { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes wa-total-pulse { 0% { transform: scale(1); } 50% { transform: scale(1.08); } 100% { transform: scale(1); } }
`;

const DiscountAppliedBanner = ({
  discounts,
}: {
  discounts: { total: number }[];
}) => {
  const { t } = useTranslation("booker");
  const [show, setShow] = useState(false);
  const prevCountRef = useRef(-1);

  const activeCount = discounts.filter(d => d.total > 0).length;

  useEffect(() => {
    // Skip the initial mount (prevCountRef === -1).
    if (prevCountRef.current === -1) {
      prevCountRef.current = activeCount;
      return;
    }
    if (activeCount > prevCountRef.current) {
      setShow(true);
      const timer = setTimeout(() => setShow(false), 2500);
      prevCountRef.current = activeCount;
      return () => clearTimeout(timer);
    }
    prevCountRef.current = activeCount;
  }, [activeCount]);

  if (!show) return null;

  return (
    <>
      <style>{DISCOUNT_KEYFRAMES}</style>
      <Flex
        w="full"
        justifyContent="center"
        py="100"
        style={{
          animation:
            "wa-discount-slide-in 300ms ease-out, wa-discount-fade-out 400ms ease-in 2100ms forwards",
        }}
      >
        <Text visual="bodySemiBold" color="text.success.mid">
          {t("CHECKOUT.DISCOUNT_APPLIED")}
        </Text>
      </Flex>
    </>
  );
};

const AnimatedTotal = ({ totalDue }: { totalDue: number }) => {
  const [animKey, setAnimKey] = useState(0);
  const prevRef = useRef(totalDue);

  useEffect(() => {
    if (prevRef.current !== totalDue && prevRef.current > 0 && totalDue > 0) {
      setAnimKey(k => k + 1);
    }
    prevRef.current = totalDue;
  }, [totalDue]);

  return (
    <>
      <style>{DISCOUNT_KEYFRAMES}</style>
      <Heading
        size="h2"
        key={animKey}
        style={
          animKey > 0
            ? { animation: "wa-total-pulse 400ms ease-in-out" }
            : undefined
        }
      >
        ${formatPrice(totalDue)}
      </Heading>
    </>
  );
};
